import React from "react";
import { Prompt } from "react-router";
import { RouteComponentPropsHistory } from "../../types";
import { Link } from "react-router-dom";

import NoteBox from "../NoteBox";
import OverflowMenuItem from "../OverflowMenuItem";
import ToolbarMenuItem from "../ToolbarMenuItem";
import ViewToolbar from "../ViewToolbar";

import { V2 } from "../../Api";
import { formatDateTimeLocal, formatName } from "../../Utilities";
import { IAuthProps } from "../AuthContext";
import withAuth from "../withAuth";
import withTitle, { IViewTitleProps } from "../withTitle";

import "../../form.css";
import "../../tasks.css";
import "../viewer.css";

interface ITaskListTaskViewState {
    changedFields: string[];
    hasChanged: boolean;
    isLoading: boolean | string;
    submitStatus: boolean | string;

    task: any;

    notes: any[];

    userId?: string;

    [name: string]: any;
}

class TaskListTaskView extends React.Component<RouteComponentPropsHistory<any, any> & IViewTitleProps & IAuthProps, ITaskListTaskViewState> {
    constructor(props: Readonly<RouteComponentPropsHistory<any, any> & IViewTitleProps & IAuthProps>) {
        super(props);

        const state: ITaskListTaskViewState = {
            changedFields: [],
            hasChanged: false,
            isLoading: true,
            submitStatus: false,

            task: {},

            notes: [],
        };

        if (props.location.state) {
            state.userId = props.location.state.userId;
        }

        this.state = state;

        this.addNote = this.addNote.bind(this);
        this.handleEditorVisible = this.handleEditorVisible.bind(this);
        this.completeTask = this.completeTask.bind(this);
    }

    public async componentDidMount() {
        try {
            const params = [];
            if (this.state.userId) {
                params.push(`user=${this.state.userId}`);
            }

            const task = await V2.get<any>(`tasks/${this.props.match.params.id}?${params.join("&")}`);

            this.setState({
                isLoading: false,
                task,

                notes: task.notes.map((a: any) => { a.author = formatName(a.employee.name); return a; })
            });

            this.props.viewTitleContext.changeTitle(`Task - ${task.subject}`, "both");
        }
        catch (e) {
            this.setState({
                isLoading: e.message
            });
        }
    }

    public render() {
        if (this.state.isLoading === true) {
            return <div className="spinner-center lds-dual-ring-big" />;
        }
        else if (typeof this.state.isLoading === "string") {
            return <span className="error-text">{this.state.isLoading}</span>;
        }

        return (
            <div className="viewer-root with-actionbar">
                <Prompt when={this.state.hasChanged} message="Your changes will not be saved if you continue." />
                <ViewToolbar>
                    <OverflowMenuItem>
                        <ToolbarMenuItem right="tasks:write" condition={this.state.task.status !== 0}>
                            <button type="button" onClick={this.completeTask}><i className="icon-minus" /><span>Re-open task</span></button>
                        </ToolbarMenuItem>
                    </OverflowMenuItem>
                </ViewToolbar>
                <section className="info-block">
                    <header className="ellipse">
                        <h3>Task &mdash; {this.state.task.subject}</h3>
                    </header>

                    <dl className="info-list">
                        <div className="info-list-row">
                            <div>
                                <dt>Subject</dt>
                                <dd>{this.state.task.subject}</dd>
                            </div>
                        </div>
                        <div className="info-list-row">
                            <div className="col-75">
                                <dt>Date & Time Created</dt>
                                <dd>{formatDateTimeLocal(this.state.task.timestamp)}</dd>
                            </div>
                            <div className="col-25">
                                <dt>Status</dt>
                                <dd>{this.statusToString(this.state.task.status)}</dd>
                            </div>
                        </div>
                        {
                            this.state.task.timestampCompleted
                                ? <div className="info-list-row">
                                    <div>
                                        <dt>Date & Time Completed</dt>
                                        <dd>{formatDateTimeLocal(this.state.task.timestampCompleted)}</dd>
                                    </div>
                                </div>
                                : ""
                        }
                        <div className="info-list-row">
                            <div>
                                <dt>Attached Items</dt>
                                <dd>
                                    {
                                        this.state.task.attachments && this.state.task.attachments.length > 0
                                            ? <ul className="tasks-attachment-list">
                                                {this.state.task.attachments.map((a: any) => <li key={a.id}>{this.formatAttachment(a)}</li>)}
                                            </ul>
                                            : "None"
                                    }
                                </dd>
                            </div>
                        </div>
                    </dl>
                </section>
                &nbsp;
                <section className="info-block">
                    <header>
                        <h3>Notes</h3>
                    </header>

                    <NoteBox displayName={this.props.authContext.user.displayName} notes={this.state.notes} onNewNote={this.addNote} onEditorChange={this.handleEditorVisible} hideControls={this.state.task.status !== 0 || !!this.state.userId} />
                </section>
                &nbsp;
                {
                    this.state.submitStatus === true
                        ? <div className="spinner lds-dual-ring" />
                        : typeof this.state.submitStatus === "string"
                            ? <span className="error-text">{this.state.submitStatus}</span>
                            : ""
                }
                {
                    this.state.task.status === 0
                        ? <div className="action-panel">
                            <button type="button" className="button push-right text-with-icon" onClick={this.completeTask} disabled={!!this.state.userId}><i className="icon-check" /><span>Complete Task</span></button>
                        </div>
                        : ""
                }
            </div>
        );
    }

    private statusToString(status: number) {
        switch (status) {
            case 0: return "Active";
            case 1: return "Completed";
            default: return `Unknown (${status})`;
        }
    }

    private async addNote(body: string) {
        const note = await V2.post<any>(`tasks/${this.state.task.id}/notes`, { body });
        note.author = formatName(note.employee.name);

        this.setState(s => {
            s.notes.push(note);

            return { notes: s.notes }
        });
    }

    private handleEditorVisible(isVisible: boolean) {
        this.setState({
            hasChanged: isVisible
        });
    }

    private formatAttachment(attach: any) {
        switch (attach.type) {
            case "survey": return <Link to={`/surveys/${attach.surveyId}`}>Questionnaire - {attach.displayText}</Link>;
            case "fair": return <Link to={`/fairs/${attach.fairId}`}>Health Fair - {attach.displayText}</Link>;
            case "appointment": return <Link to={`/appointments/${attach.appointmentId}`}>Appointment - {attach.displayText}</Link>;
            case "surgery": return <Link to={`/surgery/${attach.surgeryId}`}>Surgery - {attach.displayText}</Link>;
            default: return `${attach.type} ${attach.id} ${attach.displayText}`;
        }
    }

    private async completeTask() {
        try {
            this.setState({
                submitStatus: true,
            });

            await V2.post(`tasks/${this.state.task.id}/status`, { status: this.state.task.status === 0 ? "completed" : "active" });

            this.props.history.goBack();
        }
        catch (e) {
            this.setState({
                submitStatus: e.message
            });
        }
    }
}

export default withAuth(withTitle("Task", TaskListTaskView));
